import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ProjectProvider } from './context/ProjectContext';
import { UserStatus } from './types';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Dashboard from './views/Dashboard';
import Results from './views/Results';
import History from './views/History';
import Ingestion from './views/Ingestion';
import Configuration from './views/Configuration';
import Users from './views/Users';
import Login from './views/Login';
import Billing from './views/Billing';
import Landing from './views/Landing';
import Security from './views/Security';
import Privacy from './views/Privacy';
import Runner from './views/Runner';
import Clients from './views/Clients';
import Projects from './views/projects/Projects';
import OAuthCallback from './views/OAuthCallback';
import PendingApproval from './views/PendingApproval';
import ProjectsNew from './views/projects/ProjectsNew';
import ProjectHome from './views/projects/ProjectHome';
import Entities from './views/entities/Entities';
import EntityDetail from './views/entities/EntityDetail';
import AIGateway from './views/admin/AIGateway';
import { LLMAssignments } from './views/admin/LLMAssignments';
import ScanDetail from './views/skope/ScanDetail';
import SkopeDashboard from './views/skope/SkopeDashboard';
import PerformanceDashboard from './views/performance/PerformanceDashboard';
import ExecutionDetail from './views/performance/ExecutionDetail';

const Layout = () => {
  return (
    <div className="flex h-screen w-full bg-background-dark overflow-hidden font-sans">
      <Sidebar />
      <main className="flex-1 flex flex-col min-w-0 overflow-y-auto">
        <HeaderWrapper />
        <div className="p-4 md:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

const HeaderWrapper = () => {
  // Use location to determine title
  const location = useLocation();

  const getHeaderTitle = (pathname: string) => {
    switch (pathname) {
      case '/settings': return 'Configuración';
      case '/users': return 'Gestión de Usuarios';
      case '/results': return 'Results';
      case '/history': return 'History';
      case '/ingestion': return 'Ingestion';
      case '/billing': return 'Facturación y Planes';
      case '/dashboard': return 'Dashboard';
      case '/clients': return 'Gestión de Clientes';
      case '/admin/ai-gateway': return 'AI Gateway';
      case '/admin/llm-assignments': return 'Asignaciones LLM';
      default:
        if (pathname === '/skope') return 'Skope — Accesibilidad WCAG';
        if (pathname.startsWith('/scans/')) return 'Skope Scan';
        if (pathname === '/performance') return 'Performance K6';
        if (pathname.startsWith('/performance/executions/')) return 'Detalle Ejecución K6';
        return 'Dashboard';
    }
  };

  return (
    <Header
      title={getHeaderTitle(location.pathname)}
    />
  );
};

// Protected route wrapper that checks user status
const ProtectedLayout = () => {
  const { user } = useAuth();

  // If user is pending, redirect to pending approval page
  if (user && user.status === UserStatus.PENDING) {
    return <Navigate to="/pending" replace />;
  }

  return <Layout />;
};

const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background-dark flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={isAuthenticated ? <Navigate to="/dashboard" /> : <Landing />} />
      <Route path="/security" element={<Security />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/runner" element={<Runner />} />
      <Route path="/login" element={!isAuthenticated ? <Login onSuccess={() => { }} /> : <Navigate to="/dashboard" />} />
      <Route path="/auth/callback" element={<OAuthCallback />} />
      <Route path="/pending" element={isAuthenticated ? <PendingApproval /> : <Navigate to="/login" />} />

      <Route element={isAuthenticated ? <ProtectedLayout /> : <Navigate to="/" />}>

        {/* Global/Admin Routes */}
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/users" element={<Users />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/clients" element={<Clients />} />
        <Route path="/admin/ai-gateway" element={<AIGateway />} />
        <Route path="/admin/llm-assignments" element={<LLMAssignments />} />

        {/* Project Context Routes */}
        <Route path="/projects" element={<Projects />} />
        <Route path="/projects/new" element={<ProjectsNew />} />

        {/* Workspace Routes (Nested) */}
        <Route path="/projects/:projectId" element={<ProjectHome />} />
        <Route path="/projects/:projectId/entities" element={<Entities />} />
        <Route path="/projects/:projectId/entities/:entityId" element={<EntityDetail />} />
        <Route path="/projects/:projectId/ingestion" element={<Ingestion />} />
        <Route path="/projects/:projectId/history" element={<History />} />
        <Route path="/projects/:projectId/results" element={<Results />} />
        <Route path="/projects/:projectId/settings" element={<Configuration />} />

        {/* Skope Scans */}
        <Route path="/skope" element={<SkopeDashboard />} />
        <Route path="/scans/:scanId" element={<ScanDetail />} />

        {/* Performance / K6 Routes */}
        <Route path="/performance" element={<PerformanceDashboard />} />
        <Route path="/performance/executions/:executionId" element={<ExecutionDetail />} />

        {/* Legacy Redirects (Optional: keep for backward compat or remove) */}
        <Route path="/entities" element={<Navigate to="/projects" replace />} />
        <Route path="/ingestion" element={<Navigate to="/projects" replace />} />
        <Route path="/settings" element={<Navigate to="/projects" replace />} />
      </Route>

      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};


const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <ProjectProvider>
          <AppContent />
        </ProjectProvider>
      </Router>
    </AuthProvider>
  );
};

export default App;
